<script setup>
import { ref, onMounted, onUnmounted } from 'vue';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import BoundedComponent from '@/components/BoundedComponent.vue';
import TrafficMessage from '@/components/TrafficMessage.vue';
import { useReducedMotion } from '@/composables/useReducedMotion';

import MouseScrollingJSON from '@/lotties/scroll.json';

const messages = {
  red: {
    heading: 'Stoppa',
    content: [
      '... og staldra við þegar að við rekumst á eitthvað sjokkerandi, ótrúlegt eða vafasamt.',
      'Ekki bara skrolla áfram!',
    ],
  },

  yellow: {
    heading: 'Hugsa',
    content: [
      'Hver póstar þessu?',
      'Hvaða heimildir eru notaðar?',
      'Er verið að reyna að vekja upp einhver viðbrögð? Finnur þú fyrir reiði?',
      'Er þetta einum of ótrúlegt?',
      'Er myndin trúverðug?',
    ],
  },
  green: {
    heading: 'Athuga',
    content: [
      'Eru fleiri að fjalla um efnið, t.d. faglegir fréttamiðlar?',
      'Niðurstöður leitarvéla/staðreyndavöktunar',
      'Eigendur miðils, ritstjórn og þann sem póstar',
      'URL, https eða vefslóð',
      'Aðstoð frá öðrum',
    ],
  },
};

const sectionRef = ref();

const headerRef = ref();
const mouseScrollLottieRef = ref();
const trafficLightRef = ref();

const trafficLightRedRef = ref([]);
const trafficLightYellowRef = ref([]);
const trafficLightGreenRef = ref([]);

const redMessageRef = ref();
const yellowMessageRef = ref();
const greenMessageRef = ref();

let tlScroll;

let trafficTimeline;
let messageTimeline;

const addTlIn = (tl, node, color) => {
  tl.addLabel(`${color}-in`)
    .to(node, {
      opacity: 1,
      rotate: 0,
      scale: 1,
      y: 0,
      x: 0,
      duration: 0.3,
      ease: 'power3.in',
    })
    .addPause('+=0');
};

const addTlOut = (tl, node, color) => {
  tl.addLabel(`${color}-out`)
    .to(node, {
      opacity: 0,
      duration: 0.4,
      ease: 'power2.in',
    })
    .addPause('+=0');
};

const played = { red: false, yellow: false, green: false };

let playedFirstTime = false;

const { prefersReducedMotion } = useReducedMotion();

onMounted(() => {
  gsap.set(trafficLightYellowRef.value, { opacity: 0, duration: 0 }, 0);
  gsap.set(trafficLightGreenRef.value, { opacity: 0, duration: 0 }, 0);

  const presetMessage = (ref) => {
    gsap.set(
      ref,
      {
        opacity: 0,
        rotate: prefersReducedMotion.value ? 0 : 5,
        scale: prefersReducedMotion.value ? 1 : 0.1,
        y: prefersReducedMotion.value ? 0 : 10,
        x: prefersReducedMotion.value ? 0 : -10,
        duration: 0,
      },
      0,
    );
  };

  presetMessage(redMessageRef.value.innerRef);
  presetMessage(yellowMessageRef.value.innerRef);
  presetMessage(greenMessageRef.value.innerRef);

  trafficTimeline = gsap.timeline().pause();
  addTlIn(trafficTimeline, trafficLightYellowRef.value, 'yellow');
  addTlIn(trafficTimeline, trafficLightGreenRef.value, 'green');

  addTlOut(trafficTimeline, trafficLightGreenRef.value, 'green');
  addTlOut(trafficTimeline, trafficLightYellowRef.value, 'yellow');

  messageTimeline = gsap.timeline().pause();
  addTlIn(messageTimeline, redMessageRef.value.innerRef, 'red');
  addTlOut(messageTimeline, redMessageRef.value.innerRef, 'red');

  addTlIn(messageTimeline, yellowMessageRef.value.innerRef, 'yellow');
  addTlOut(messageTimeline, yellowMessageRef.value.innerRef, 'yellow');

  addTlIn(messageTimeline, greenMessageRef.value.innerRef, 'green');
  addTlOut(messageTimeline, greenMessageRef.value.innerRef, 'green');

  const mm = gsap.matchMedia();

  gsap.set(mouseScrollLottieRef.value, { opacity: 0, y: 30 });

  mm.add('(max-width: 767px)', () => {
    gsap.set(trafficLightRef.value, { opacity: 0, y: 30 });
  });

  // header/trafficLight show/hide animation
  const toggleSection = (section, direction) => {
    let result;
    mm.add('(max-width: 767px)', () => {
      if (direction === 1) {
        result = gsap.to(section, {
          opacity: 0,
          y: -30,
          duration: 0.5,
          height: 0,
          marginBottom: 0,
        });
      } else {
        result = gsap.to(section, {
          opacity: 1,
          y: 0,
          duration: 0.6,
          height: 'auto',
          marginBottom: 24,
        });
      }
    });

    return result;
  };

  // scroll timeline
  tlScroll = gsap.timeline({
    scrollTrigger: {
      trigger: sectionRef.value.innerRef,
      start: window.innerWidth > 1024 ? 'top-=100 top' : 'top-=10 top',
      end: 'bottom+=1000 top',
      pin: true,
      scrub: true,
      pinSpacing: true,
      onEnter: () => {
        gsap.to(mouseScrollLottieRef.value, {
          opacity: 1,
          y: 0,
          duration: 1,
          delay: 0.2,
        });
      },
      onUpdate: (self) => {
        const direction = self.direction;
        const progress = self.progress;

        // forward
        if (direction === 1) {
          if (progress > 0.1 && progress < 0.2) {
            toggleSection(headerRef.value, direction);
            toggleSection(trafficLightRef.value, -direction);
          }
          // red
          if (progress < 0.2 && played.red) {
            played.red = false;
          }
          if (progress > 0.2 && progress < 0.45 && !played.red) {
            if (!playedFirstTime) {
              messageTimeline.play('red-in');
              playedFirstTime = true;
            }
            played.red = true;
          }
          if (progress > 0.45 && played.red) {
            played.red = false;
          }

          // yellow
          if (progress > 0.45 && progress < 0.7 && !played.yellow) {
            messageTimeline.play('yellow-in');
            trafficTimeline.play('yellow-in');
            played.yellow = true;
          }
          if (progress > 0.7 && played.yellow) {
            played.yellow = false;
          }

          // green
          if (progress > 0.7 && progress < 0.95 && !played.green) {
            messageTimeline.play('green-in');
            trafficTimeline.play('green-in');
            played.green = true;
          }
          if (progress > 0.95 && played.green) {
            played.green = false;
          }
        }
        // reverse
        if (direction === -1) {
          if (progress < 0.1) {
            toggleSection(headerRef.value, direction);
            toggleSection(trafficLightRef.value, -direction);
          }
          if (progress > 0.2 && progress < 0.45 && !played.red) {
            messageTimeline.play('red-in');
            played.red = true;
          }
          if (progress < 0.2 && played.red) {
            played.red = false;
          }
          // yellow
          if (progress > 0.45 && progress < 0.7 && !played.yellow) {
            messageTimeline.play('yellow-in');
            played.yellow = true;
          }
          if (progress < 0.45 && played.yellow) {
            played.yellow = false;
            trafficTimeline.play('yellow-out');
          }
          // green
          if (progress > 0.7 && progress < 0.95 && !played.green) {
            played.green = true;
          }
          if (progress < 0.7 && played.green) {
            trafficTimeline.play('green-out');
            played.green = false;
          }
        }
      },
    },
  });
});

onUnmounted(() => {
  tlScroll?.kill();
  gsap.globalTimeline.clear();
  ScrollTrigger.getAll().forEach((t) => t.kill());
});
</script>

<template>
  <BoundedComponent
    is="section"
    ref="sectionRef"
    class-name="mb-50"
    child-class-name="bg-red rounded-[40px] pt-8 px-6 pb-8 md:pb-13 relative flex flex-col items-center h-svh sm:h-auto"
  >
    <h2 ref="headerRef" v-bounce-in class="yellow mb-6">
      Innihaldið skiptir máli
    </h2>
    <p v-bounce-in class="text-white max-w-[750px] text-center mb-16">
      Netið er yfirfullt af upplýsingum - fréttum, myndböndum og færslum sem
      berast okkur úr öllum áttum. En ekki er allt efni jafngott eða
      áreiðanlegt. Sumt er gagnlegt og byggir á staðreyndum, annað er villandi
      eða jafnvel vísvitandi rangt. Þess vegna skiptir gagnrýnin hugsun
      lykilmáli: að stoppa, hugsa og athuga.
    </p>
    <!-- traffic light -->
    <div class="items-center justify-center flex grow relative md:mt-10 w-full sm:mb-10 select-none">
      <div ref="trafficLightRef" class="absolute bottom-0 md:static -left-18">
        <div class="md:relative w-32 h-75 mx-auto">
          <div class="sm:scale-120 w-30 h-75 relative">
            <img
              ref="trafficLightRedRef"
              src="/images/traffic-light.png"
              alt="traffic light"
              class="object-cover w-full h-full shrink-0 absolute z-[1] object-[-13px_center]"
              loading="lazy"
              width="120"
              height="300"
            />
            <img
              ref="trafficLightYellowRef"
              src="/images/traffic-light.png"
              alt="traffic light"
              class="object-cover w-full h-full shrink-0 absolute z-[2] object-center"
              loading="lazy"
              width="120"
              height="300"
            />
            <img
              ref="trafficLightGreenRef"
              src="/images/traffic-light.png"
              alt="traffic light"
              class="object-cover w-full h-full shrink-0 absolute z-[3] object-[-242px_center]"
              loading="lazy"
              width="120"
              height="300"
            />
          </div>
          <TrafficMessage
            ref="redMessageRef"
            :message="messages.red"
            class="-top-21 md:-top-24 left-14 md:left-19 origin-bottom-left"
          />
          <TrafficMessage
            ref="yellowMessageRef"
            :message="messages.yellow"
            class="-top-2 md:right-18 left-14 md:left-auto origin-bottom-left md:origin-bottom-right"
            :invert-x="true"
          />
          <TrafficMessage
            ref="greenMessageRef"
            :message="messages.green"
            class="bottom-18 md:bottom-16 md:left-16 left-14 origin-bottom-left"
          />
        </div>
      </div>
    </div>
    <!-- mouse scroll lottie -->
    <div ref="mouseScrollLottieRef" class="relative">
      <Vue3Lottie
        :animationData="MouseScrollingJSON"
        :width="40"
        :height="80"
        role="img"
        aria-label="skruna niður"
      />
    </div>
  </BoundedComponent>
</template>
